"use client";

import Image from "next/image";
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Clock,
  MapPin,
  User,
  Phone,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Navigation,
  Wrench,
  UserPlus,
  BarChart3,
  PhoneCall,
  UserCheck,
  Car,
  MapPinned,
  CheckCircle2,
  Star,
} from "lucide-react";
import { GratitudeComposer } from "@/components/gratitude-composer";
import { VolunteerRecruitment } from "@/components/volunteer-recruitment";
import { ScenarioSelector } from "@/components/scenario-selector";
import { EventSummary } from "@/components/event-summary";
import { GratitudeSuccess } from "@/components/gratitude-success";
import { testScenarios, getScenario } from "@/lib/test-scenarios";

interface Event {
  id: string;
  type: string;
  location: string;
  status: "opened" | "assigned" | "en_route" | "arrived" | "completed";
  volunteer?: {
    name?: string;
    eta?: number;
    branch?: string;
    eventsCompleted?: number;
  };
  createdAt: Date;
  updatedAt: Date;
  description: string;
  statusHistory?: {
    status: string;
    timestamp: Date;
  }[];
  eventPhotos?: string[];
}

const statusConfig = {
  opened: {
    label: "נפתח",
    color: "bg-orange-500",
    borderColor: "border-orange-500",
    bgLight: "bg-orange-50",
    icon: PhoneCall,
    progress: 20,
    description: "הקריאה נפתחה ומחכה להקצאת מתנדב",
  },
  assigned: {
    label: "הוקצה מתנדב",
    color: "bg-blue-500",
    borderColor: "border-blue-500",
    bgLight: "bg-blue-50",
    icon: UserCheck,
    progress: 40,
    description: "מתנדב הוקצה לטיפול בקריאה",
  },
  en_route: {
    label: "בדרך",
    color: "bg-cyan-600",
    borderColor: "border-cyan-600",
    bgLight: "bg-cyan-50",
    icon: Car,
    progress: 70,
    description: "המתנדב בדרך למקום",
  },
  arrived: {
    label: "הגיע למקום",
    color: "bg-purple-600",
    borderColor: "border-purple-600",
    bgLight: "bg-purple-50",
    icon: MapPinned,
    progress: 90,
    description: "המתנדב הגיע למקום ומטפל בבעיה",
  },
  completed: {
    label: "הושלם",
    color: "bg-green-600",
    borderColor: "border-green-600",
    bgLight: "bg-green-50",
    icon: CheckCircle2,
    progress: 100,
    description: "הטיפול הושלם בהצלחה",
  },
};

// Demo data
const demoEvent: Event = {
  id: "EVT-2024-001",
  type: "תקר בגלגל",
  location: "רחוב הרצל 45, תל אביב",
  status: "completed", // Changed to completed to show gratitude system
  volunteer: {
    eta: 8,
    branch: "ירושלים",
    eventsCompleted: 89,
  },
  createdAt: new Date(Date.now() - 45 * 60 * 1000), // 45 minutes ago
  updatedAt: new Date(Date.now() - 5 * 60 * 1000), // 5 minutes ago
  description: "תקר בגלגל קדמי ימני, הרכב חונה בצד הדרך",
  statusHistory: [
    { status: "opened", timestamp: new Date(Date.now() - 45 * 60 * 1000) },
    { status: "assigned", timestamp: new Date(Date.now() - 40 * 60 * 1000) },
    { status: "en_route", timestamp: new Date(Date.now() - 35 * 60 * 1000) },
    { status: "arrived", timestamp: new Date(Date.now() - 20 * 60 * 1000) },
    { status: "completed", timestamp: new Date(Date.now() - 5 * 60 * 1000) },
  ],
  eventPhotos: ["/placeholder.jpg"],
};

export default function EventSessionsPage() {
  const [event, setEvent] = useState<Event | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showGratitude, setShowGratitude] = useState(false); // Added state for gratitude modal
  const [showVolunteerRecruitment, setShowVolunteerRecruitment] =
    useState(false); // Added state for volunteer recruitment
  const [gratitudeSent, setGratitudeSent] = useState(false); // Track if gratitude was sent
  const [showEventSummary, setShowEventSummary] = useState(false); // Added state for event summary
  const [showGratitudeSuccess, setShowGratitudeSuccess] = useState(false); // Added state for gratitude success
  const [gratitudeData, setGratitudeData] = useState<{ type: string; content: string; rating: number } | null>(null); // Store gratitude data

  // Developer mode states
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);
  const [currentScenario, setCurrentScenario] = useState("completed");

  // Handle keyboard shortcut for developer mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setIsDeveloperMode((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  // Handle scenario change
  const handleScenarioChange = (scenarioKey: string) => {
    const newEvent = getScenario(scenarioKey);
    setEvent(newEvent);
    setCurrentScenario(scenarioKey);
    setGratitudeSent(false); // Reset gratitude state when changing scenarios
    setGratitudeData(null); // Reset gratitude data
    setLastRefresh(new Date());
    setCurrentTime(Date.now());
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setLastRefresh(new Date());
    setCurrentTime(Date.now());
    setIsRefreshing(false);
  };

  const formatTimeAgo = useCallback((date: Date) => {
    const minutes = Math.floor((currentTime - date.getTime()) / (1000 * 60));
    if (minutes < 1) return "עכשיו";
    if (minutes === 1) return "לפני דקה";
    if (minutes < 60) return `לפני ${minutes} דקות`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return "לפני שעה";
    return `לפני ${hours} שעות`;
  }, [currentTime]);

  const handleGratitudeSent = (data: {
    type: string;
    content: string;
    rating: number;
  }) => {
    setGratitudeSent(true);
    setGratitudeData(data);
    setShowGratitude(false);
    // Show success message
    setShowGratitudeSuccess(true);
  };

  // Show default empty state when no event
  if (!event) {
    return (
      <div
        className="min-h-screen bg-linear-to-b from-orange-50 to-amber-50 flex items-center justify-center p-4"
        dir="rtl"
      >
        <div className="max-w-md w-full">
          <Card className="border-2 border-orange-200 shadow-xl">
            <CardContent className="p-8 text-center space-y-6">
              <div className="flex justify-center mb-4">
                <Image
                  src="/logo-1230.png"
                  alt="Yedidim Logo"
                  width={240}
                  height={96}
                  className="h-24 w-auto"
                  priority
                />
              </div>

              <div className="space-y-3">
                <h1 className="text-3xl font-serif font-black text-orange-900">
                  ידידים - סיוע בדרכים
                </h1>
                <p className="text-lg text-orange-800 font-medium">
                  לא נמצא אירוע 🔍
                </p>
              </div>

              <div className="bg-orange-100 border border-orange-300 rounded-lg p-4">
                <p className="text-sm text-orange-900">
                  הדף הזה מיועד לצפייה באירוע ספציפי.
                  <br />
                  אנא וודא שיש לך קישור תקין עם מזהה אירוע.
                </p>
              </div>

              <div className="pt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  צריכים עזרה? צרו קשר עם המוקד שלנו:
                </p>
                <Button
                  size="lg"
                  className="w-full bg-linear-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                  onClick={() => (window.location.href = "tel:1230")}
                >
                  <Phone className="h-5 w-5 ml-2" />
                  חייג 1230
                </Button>
              </div>

              <p className="text-xs text-muted-foreground italic pt-4">
                שירות כרוך בחיוך 💛
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentStatus = statusConfig[event.status];
  const StatusIcon = currentStatus.icon;

  return (
    <div className="min-h-screen bg-muted p-4">
      {/* Developer Mode Scenario Selector */}
      {isDeveloperMode && (
        <ScenarioSelector
          currentScenario={currentScenario}
          onScenarioChange={handleScenarioChange}
          onClose={() => setIsDeveloperMode(false)}
          event={event}
        />
      )}

      <div className="max-w-md mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Image
              src="/logo-1230.png"
              alt="Yedidim Logo"
              width={160}
              height={64}
              className="h-12 w-auto"
              priority
            />
            <div>
              <h1 className="text-xl font-serif font-black text-foreground">
                ידידים - סיוע בדרכים 24/6
              </h1>
              <p className="text-xs text-accent font-medium">
                שירות כרוך בחיוך 😊
              </p>
              <p className="text-xs text-muted-foreground">
                עדכון אחרון: {formatTimeAgo(lastRefresh)}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            disabled={isRefreshing}
            className="gap-2 bg-transparent"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            רענן
          </Button>
        </div>

        {/* Event Card */}
        <Card className="border-2 border-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif font-bold">
                {event.type}
              </CardTitle>
              <Badge variant="secondary" className="font-mono text-xs">
                {event.id}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {event.location}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">{event.description}</p>

            {/* Current Status Display */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2.5 rounded-full ${currentStatus.bgLight} ${currentStatus.borderColor} border-2`}
                >
                  <StatusIcon
                    className={`h-5 w-5 ${currentStatus.borderColor.replace(
                      "border-",
                      "text-"
                    )}`}
                  />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{currentStatus.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {currentStatus.description}
                  </p>
                </div>
              </div>

              <Progress value={currentStatus.progress} className="h-2" />

              <div className="text-xs text-muted-foreground text-center">
                {currentStatus.progress}% הושלם
              </div>
            </div>

            {/* Volunteer Info */}
            {event.volunteer && (
              <Card className="bg-accent/10 border-accent/20">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-accent" />
                    <span className="font-semibold text-sm">המתנדב שלך</span>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">
                          מתנדב ידידים
                        </span>
                        <p className="text-xs text-muted-foreground">
                          מתנדב ישראלי נותן מזמנו בשבילך
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 bg-transparent"
                        onClick={() => (window.location.href = "tel:1230")}
                      >
                        <Phone className="h-3 w-3" />
                        התקשר למוקד
                      </Button>
                    </div>

                    {event.volunteer.eta && event.status === "en_route" && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-primary" />
                        <span>זמן הגעה משוער: {event.volunteer.eta} דקות</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">ציר זמן</h3>
              <div className="space-y-2">
                {Object.entries(statusConfig).map(([status, config], index) => {
                  const isCompleted =
                    Object.keys(statusConfig).indexOf(event.status) >= index;
                  const isCurrent = status === event.status;
                  const Icon = config.icon;

                  // Find the timestamp for this status from statusHistory
                  const statusTimestamp = event.statusHistory?.find(
                    (h) => h.status === status
                  )?.timestamp;

                  return (
                    <div key={status} className="flex items-center gap-3">
                      <div
                        className={`p-1.5 rounded-full border-2 transition-all duration-300 ${
                          isCompleted
                            ? `${config.bgLight} ${config.borderColor}`
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <Icon
                          className={`h-3.5 w-3.5 ${
                            isCompleted
                              ? config.borderColor.replace("border-", "text-")
                              : "text-gray-400"
                          }`}
                        />
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <span
                          className={`text-sm ${
                            isCurrent
                              ? "font-semibold text-foreground"
                              : isCompleted
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }`}
                        >
                          {config.label}
                        </span>
                        {statusTimestamp && (
                          <span className="text-xs text-muted-foreground">
                            {formatTimeAgo(statusTimestamp)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Updated completion card to show gratitude sent state */}
            {event.status === "completed" && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-green-900">
                      {gratitudeSent
                        ? "תודה נשלחה בהצלחה!"
                        : "🎉 הגעת בשלום הביתה!"}
                    </span>
                  </div>
                  {!gratitudeSent ? (
                    <>
                      <p className="text-sm text-green-800">
                        מתנדב ידידים נתן מזמנו בחינם כדי לעזור לך 💚
                      </p>
                      <p className="text-xs text-green-700 font-medium">
                        זה הקסם של ידידים - ישראלים עוזרים לישראלים בחיוך 😊
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          onClick={() => setShowEventSummary(true)}
                          variant="outline"
                          className="flex-1"
                        >
                          צפה בסיכום
                        </Button>
                        <Button
                          onClick={() => setShowGratitude(true)}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          שלח תודה
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-green-800 font-medium">
                        התודה נשלחה בהצלחה! 🙏
                      </p>
                      {gratitudeData && (
                        <div className="bg-white/50 rounded-lg p-3 border border-green-300">
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs font-medium">
                              דירוג: {gratitudeData.rating}/5
                            </span>
                          </div>
                          <p className="text-xs text-green-700">
                            <strong>סוג הודעה:</strong>{" "}
                            {gratitudeData.type === "text"
                              ? "טקסט"
                              : gratitudeData.type === "voice"
                              ? "הודעת קול"
                              : "הודעת וידאו"}
                          </p>
                          {gratitudeData.type === "text" && (
                            <p className="text-sm text-green-800 mt-2 italic">
                              &ldquo;{gratitudeData.content}&rdquo;
                            </p>
                          )}
                          {gratitudeData.type !== "text" && (
                            <p className="text-xs text-green-700 mt-1">
                              {gratitudeData.content}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            {event.status !== "completed" && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  className="w-full bg-transparent gap-2"
                  onClick={() => (window.location.href = "tel:1230")}
                >
                  <Phone className="h-4 w-4" />
                  התקשר למוקד - 1230
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        {event.status !== "completed" && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-500 rounded-full">
                  <Wrench className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900 mb-1">
                    טיפים לזמן ההמתנה
                  </h3>
                  <p className="text-sm text-blue-800">
                    הישאר במקום בטוח, הדלק פנסי חירום אם חשוך, ושמור על קשר עם
                    המתנדב.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Volunteer recruitment card - only show after event completion */}
        {event.status === "completed" && (
          <Card className="bg-accent/10 border-accent/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-accent rounded-full">
                  <UserPlus className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-accent mb-1">
                    הצטרף כמתנדב
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    עזור לאחרים בקהילה שלך. הצטרף לרשת המתנדבים והיה חלק
                    מהשינוי.
                  </p>
                  <Button
                    onClick={() => setShowVolunteerRecruitment(true)}
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-transparent border-accent text-accent hover:bg-accent hover:text-white"
                  >
                    <UserPlus className="h-3 w-3" />
                    הירשם כמתנדב
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {showGratitude && (
          <GratitudeComposer
            eventId={event.id}
            onClose={() => setShowGratitude(false)}
            onSent={handleGratitudeSent} // Added callback for when gratitude is sent
          />
        )}

        {/* Added volunteer recruitment modal */}
        {showVolunteerRecruitment && (
          <VolunteerRecruitment
            onClose={() => setShowVolunteerRecruitment(false)}
          />
        )}

        {/* Event Summary Modal */}
        {showEventSummary && event.volunteer && (
          <EventSummary
            event={event}
            onClose={() => setShowEventSummary(false)}
            onThankYou={() => {
              setShowEventSummary(false);
              setShowGratitude(true);
            }}
          />
        )}

        {/* Gratitude Success Modal */}
        {showGratitudeSuccess && event.volunteer && (
          <GratitudeSuccess onClose={() => setShowGratitudeSuccess(false)} />
        )}
      </div>
    </div>
  );
}
